// components/tier-branch/BranchUsersDialog.tsx
// Users in a branch: paginated list with multi-select, "Add user(s)" by id,
// and "Move selected" → TransferUserDialog.
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField,
  CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, Pagination, Typography, Stack,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { Branch, TierBranchUser } from "../../types/tierBranch";
import type { Notify } from "./useSnack";
import TransferUserDialog from "./TransferUserDialog";

const LIMIT = 25;

const BranchUsersDialog: React.FC<{
  open: boolean;
  branch: Branch | null;
  branches: Branch[];
  onClose: () => void;
  notify: Notify;
}> = ({ open, branch, branches, onClose, notify }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TierBranchUser[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [addIds, setAddIds] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);

  const load = () => {
    if (!branch) return;
    setLoading(true);
    api
      .getBranchUsers(branch.id, { page, limit: LIMIT })
      .then((r) => {
        setRows(r.data);
        setTotalPages(r.totalPages);
      })
      .catch((e: any) => notify(e?.message || "Failed to load users", "error"))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [open, branch, page]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setSelected(new Set());
      setAddIds("");
    }
  }, [open, branch]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAdd = async () => {
    if (!branch) return;
    const ids = addIds
      .split(/[,\s]+/)
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) {
      notify("Enter one or more numeric user IDs", "warning");
      return;
    }
    try {
      await api.addUsersToBranch(branch.id, { userIds: ids });
      notify(`Added ${ids.length} user(s)`, "success");
      setAddIds("");
      load();
    } catch (e: any) {
      notify(e?.message || "Failed to add users", "error");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{branch ? `Users in ${branch.name}` : "Users"}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="Add user IDs (comma separated)"
              size="small"
              fullWidth
              value={addIds}
              onChange={(e) => setAddIds(e.target.value)}
            />
            <Button variant="outlined" onClick={handleAdd}>
              Add
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Typography color="text.secondary">No users in this branch.</Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>User</TableCell>
                    <TableCell align="right">Tier</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(u.id)}
                          onChange={() => toggle(u.id)}
                        />
                      </TableCell>
                      <TableCell>{u.username}</TableCell>
                      <TableCell align="right">{u.tierId ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={selected.size === 0}
            onClick={() => setTransferOpen(true)}
          >
            Move selected ({selected.size})
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <TransferUserDialog
        open={transferOpen}
        userIds={Array.from(selected)}
        fromBranchId={branch?.id ?? null}
        branches={branches}
        onClose={() => setTransferOpen(false)}
        onDone={() => {
          setSelected(new Set());
          load();
        }}
        notify={notify}
      />
    </>
  );
};

export default BranchUsersDialog;
